import React from "react";
import PublicNavbar from "@/components/PublicNavbar";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />
      <section className="bg-gradient-to-b from-blue-50/60 to-white border-b border-slate-200 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-brand text-xs font-bold">
            <Shield size={14} /> Legal
          </div>
          <h1 className="heading mt-5 text-4xl sm:text-5xl font-black">Privacy Policy</h1>
          <p className="mt-3 text-slate-600">Last updated: April 2026</p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-slate">
        <div className="space-y-6 text-slate-700 leading-relaxed">
          <p>PMR Welfare Foundation ("we", "our", "us") is committed to protecting the privacy of every donor, volunteer, student, beneficiary and visitor to our website and services. This Privacy Policy describes what information we collect, how we use it, and your rights.</p>

          <H>1. Information We Collect</H>
          <ul className="list-disc list-outside ml-6 space-y-2">
            <li><b>Donor Information:</b> name, email, phone, address, PAN (for 80G receipt), donation amount, payment method.</li>
            <li><b>Student/Volunteer Information:</b> name, email, phone, college/organization, skills, profile photo.</li>
            <li><b>Exam Data:</b> selfie (for identity verification), answers, time-stamps, violation logs (tab-switch, fullscreen exit).</li>
            <li><b>Technical:</b> browser type, device type, IP address, pages visited, cookies (strictly for authentication and site functionality).</li>
          </ul>

          <H>2. How We Use Information</H>
          <ul className="list-disc list-outside ml-6 space-y-2">
            <li>Issuing 80G tax-deductible donation receipts.</li>
            <li>Communicating program updates, exam schedules, internship decisions.</li>
            <li>Operating secure online exams and detecting malpractice.</li>
            <li>Processing payments through PayU and maintaining financial records for audit.</li>
            <li>Improving our programs and transparency reporting.</li>
          </ul>

          <H>3. How We Protect Information</H>
          <p>All data is transmitted over HTTPS. Passwords are hashed with bcrypt. Payment data is handled directly by PayU and never stored on our servers. Access to personal data is restricted to authorized staff on a need-to-know basis.</p>

          <H>4. Sharing of Information</H>
          <p>We do <b>not</b> sell or rent your personal information. We share data only with (a) PayU for payment processing, (b) Resend for transactional email delivery, (c) government authorities when legally required (e.g., Income Tax, FCRA, Registrar of Companies).</p>

          <H>5. Your Rights</H>
          <ul className="list-disc list-outside ml-6 space-y-2">
            <li>Request a copy of your personal data.</li>
            <li>Correct or delete your personal data (subject to legal retention obligations for donations).</li>
            <li>Unsubscribe from non-transactional emails at any time.</li>
          </ul>

          <H>6. Children's Data</H>
          <p>Beneficiary children's photographs and stories are shared only with consent from a parent/guardian and only for non-commercial awareness purposes. We never publish their full personal details.</p>

          <H>7. Cookies</H>
          <p>We use essential cookies for authentication (access & refresh tokens) and basic analytics. You can disable cookies in your browser, but this may limit dashboard access.</p>

          <H>8. Contact</H>
          <p>For privacy inquiries or data requests, write to <a className="text-brand font-bold" href="mailto:contact@pmrwelfarefoundation.in">contact@pmrwelfarefoundation.in</a> or call <b>+91 9155561892</b>.</p>

          <H>9. Changes to this Policy</H>
          <p>We may update this policy periodically. The latest version is always published on this page with the revised "Last updated" date.</p>
        </div>
      </section>
      <PublicFooter contact={null} />
      <WhatsAppButton />
    </div>
  );
}

function H({ children }) {
  return <h2 className="heading text-2xl font-black text-brand pt-4">{children}</h2>;
}
